import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getJwt } from '@jane/shared/auth';
import { api, withAuthHeader } from '@jane/shared/data-access';

import type { AuthorizationProps, DeviceGrant } from './authentication.types';
import {
  kioskAuthenticationQueryKeys,
  kioskAuthenticationUrls,
} from './authentication.util';

const kioskUserLogout = () =>
  api.delete(kioskAuthenticationUrls.userSessions());

/**
 * In most cases you should probably use the useLogoutUser hook to log out a user instead of
 * using this mutation directly. The hook ensures that updating the cart and/or resetting the
 * kiosk is also handled properly.
 */
export const useKioskUserLogout = ({
  onSuccess,
  onError,
}: {
  onError?: () => void;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: kioskUserLogout,
    mutationKey: ['kioskUserLogout'],
    onError,
    onSuccess: () => {
      queryClient.removeQueries(kioskAuthenticationQueryKeys.deviceSession());
      queryClient.removeQueries(kioskAuthenticationQueryKeys.deviceGrant());
      queryClient.invalidateQueries(kioskAuthenticationQueryKeys.kioskUser());
      onSuccess && onSuccess();
    },
  });
};

const kioskDeviceGrant = async () => {
  const jwt = getJwt();

  return await api.post<DeviceGrant>(
    kioskAuthenticationUrls.deviceGrantBeginAuth(),
    undefined,
    withAuthHeader({}, jwt)
  );
};

export const useKioskDeviceGrant = ({
  onSuccess,
  onError,
}: {
  onError?: () => void;
  onSuccess?: (deviceGrant: DeviceGrant) => void;
}) => {
  return useMutation({
    mutationFn: kioskDeviceGrant,
    mutationKey: kioskAuthenticationQueryKeys.deviceGrant(),
    onSuccess: (deviceGrant: DeviceGrant) =>
      onSuccess && onSuccess(deviceGrant),
    onError,
    retry: 3,
  });
};

const kioskDeviceAuthorization = async ({ userCode }: AuthorizationProps) => {
  const jwt = getJwt();
  await api.post<Response>(
    kioskAuthenticationUrls.deviceAuthorization(),
    { user_code: userCode },
    withAuthHeader({}, jwt)
  );
};

export const useKioskDeviceAuthorization = ({
  onSuccess,
  onError,
}: {
  onError?: (err: unknown) => unknown;
  onSuccess?: () => void;
}) =>
  useMutation({
    mutationFn: kioskDeviceAuthorization,
    mutationKey: ['kioskDeviceAuthorization'],
    onSuccess: () => {
      onSuccess && onSuccess();
    },
    onError: (error) => onError && onError(error),
  });
